import * as i0 from '@angular/core';
import { Injectable, Directive, SkipSelf, HostBinding, NgModule } from '@angular/core';
import { ReplaySubject, Subject, combineLatest } from 'rxjs';
import { map, filter, tap, takeUntil } from 'rxjs/operators';
import * as i1 from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';

// TODO: Rename these
var ScreenSize;
(function (ScreenSize) {
    ScreenSize["MOBILE"] = "MOBILE";
    ScreenSize["TABLET"] = "TABLET";
    ScreenSize["TABLET_SM"] = "TABLET_SM";
    ScreenSize["DESKTOP"] = "DESKTOP";
    ScreenSize["DESKTOP_LG"] = "DESKTOP_LG";
})(ScreenSize || (ScreenSize = {}));

const PSU_BREAKPOINTS = {
    mobile: '(max-width: 599px)',
    tabletSmall: '(min-width: 600px) and (max-width: 729px)',
    tabletLarge: '(min-width: 730px) and (max-width: 959px)',
    desktop: '(min-width: 960px)',
};

// TODO Enhancement: Provide injection token for configuring custom breakpoints to support different breakpoint
// configurations per application
class ResponsiveService {
    //  ****
    constructor(breakpointObserver) {
        this.breakpointObserver = breakpointObserver;
        //  Static context
        this.currentScreenSize = ScreenSize.DESKTOP;
        this.isMobile = false;
        this.isTabletSmall = false;
        this.isTabletLarge = false;
        this.isDesktop = true;
        this.screenSizeSubject = new ReplaySubject(1);
        this.destroy$ = new Subject();
        //  **** TODO Not convinced that this stuff belongs here with the elimination of ngrx.
        this.isMenuOpen = false;
        this.isSideSheetOpen = false;
        this.menuSubject = new ReplaySubject(1);
        this.sideSheetSubject = new ReplaySubject(1);
        this.currentScreenSize$ = this.screenSizeSubject.asObservable();
        this.isMobile$ = this.breakpointMatches(PSU_BREAKPOINTS.mobile);
        this.isTabletSmall$ = this.breakpointMatches(PSU_BREAKPOINTS.tabletSmall);
        this.isTabletLarge$ = this.breakpointMatches(PSU_BREAKPOINTS.tabletLarge);
        this.isDesktop$ = this.breakpointMatches(PSU_BREAKPOINTS.desktop);
        combineLatest(this.isMobile$, this.isTabletSmall$, this.isTabletLarge$, this.isDesktop$)
            .pipe(map(([mobile, tabletSmall, tabletLarge, desktop]) => ({ mobile, tabletSmall, tabletLarge, desktop })), filter((responsiveChange) => {
            const values = Object.values(responsiveChange);
            return values.some((breakpoints) => !!breakpoints) && values.filter((val) => !!val).length === 1;
        }), tap((responsiveChange) => {
            let screenSize = null;
            if (responsiveChange.desktop) {
                screenSize = ScreenSize.DESKTOP;
                this.isDesktop = true;
            }
            else if (responsiveChange.tabletLarge) {
                screenSize = ScreenSize.TABLET;
                this.isTabletLarge = true;
            }
            else if (responsiveChange.tabletSmall) {
                screenSize = ScreenSize.TABLET_SM;
                this.isTabletSmall = true;
            }
            else if (responsiveChange.mobile) {
                screenSize = ScreenSize.MOBILE;
                this.isMobile = true;
            }
            if (!!screenSize) {
                this.screenSizeChange(screenSize);
            }
        }), takeUntil(this.destroy$))
            .subscribe();
        this.isMenuOpen$ = this.menuSubject.asObservable();
        this.isSideSheetOpen$ = this.sideSheetSubject.asObservable();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    closeMenu() {
        this.isMenuOpen = false;
        this.menuSubject.next(this.isMenuOpen);
    }
    closeSideSheet() {
        this.isSideSheetOpen = false;
        this.sideSheetSubject.next(this.isSideSheetOpen);
    }
    openMenu() {
        this.isMenuOpen = true;
        this.menuSubject.next(this.isMenuOpen);
    }
    openSideSheet() {
        this.isSideSheetOpen = true;
        this.sideSheetSubject.next(this.isSideSheetOpen);
    }
    toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
        this.menuSubject.next(this.isMenuOpen);
    }
    toggleSideSheet() {
        this.isSideSheetOpen = !this.isSideSheetOpen;
        this.sideSheetSubject.next(this.isSideSheetOpen);
    }
    screenSizeChange(newScreenSize) {
        this.currentScreenSize = newScreenSize;
        this.screenSizeSubject.next(this.currentScreenSize);
    }
    breakpointMatches(breakpoints) {
        return this.breakpointObserver.observe(breakpoints).pipe(map((result) => result.matches));
    }
}
ResponsiveService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveService, deps: [{ token: i1.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable });
ResponsiveService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.BreakpointObserver }]; } });

class ResponsiveDirective {
    constructor(responsiveService, cdRef) {
        this.responsiveService = responsiveService;
        this.cdRef = cdRef;
        this.isDesktop = false;
        this.isTablet = false;
        this.isSmallTablet = false;
        this.isMobile = false;
        this.destroyed$ = new Subject();
    }
    ngOnInit() {
        this.responsiveService.currentScreenSize$
            .pipe(filter((size) => !!size), takeUntil(this.destroyed$))
            .subscribe((size) => {
            this.isMobile = size === ScreenSize.MOBILE;
            this.isSmallTablet = size === ScreenSize.TABLET_SM;
            this.isTablet = size === ScreenSize.TABLET;
            this.isDesktop = size === ScreenSize.DESKTOP;
            this.cdRef.markForCheck();
        });
    }
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
ResponsiveDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveDirective, deps: [{ token: ResponsiveService }, { token: i0.ChangeDetectorRef, skipSelf: true }], target: i0.ɵɵFactoryTarget.Directive });
ResponsiveDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: ResponsiveDirective, selector: "[utResponsive]", host: { properties: { "class.desktop": "this.isDesktop", "class.tablet": "this.isTablet", "class.tablet-sm": "this.isSmallTablet", "class.mobile": "this.isMobile" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[utResponsive]',
                }]
        }], ctorParameters: function () { return [{ type: ResponsiveService }, { type: i0.ChangeDetectorRef, decorators: [{
                    type: SkipSelf
                }] }]; }, propDecorators: { isDesktop: [{
                type: HostBinding,
                args: ['class.desktop']
            }], isTablet: [{
                type: HostBinding,
                args: ['class.tablet']
            }], isSmallTablet: [{
                type: HostBinding,
                args: ['class.tablet-sm']
            }], isMobile: [{
                type: HostBinding,
                args: ['class.mobile']
            }] } });

class ResponsiveModule {
}
ResponsiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ResponsiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveModule, declarations: [ResponsiveDirective], imports: [CommonModule], exports: [ResponsiveDirective] });
ResponsiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ResponsiveModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [ResponsiveDirective],
                    exports: [ResponsiveDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ResponsiveDirective, ResponsiveModule, ResponsiveService, ScreenSize };
